<template>
  <div class="advanced-filter" ref="page" id="page">
    <div class="form-content bg-light">
      <div class="drawer-header">
        <FIcon name="close" class="close-btn" @click.native="handleToggleDrawer"></FIcon>
        <label class="header-title">{{ pageTitle }}</label>
      </div>

      <div class="section">
        <div
          class="widget"
          :class="{ active: isFilled(item) }"
          v-for="(item, index) in columns"
          :key="`widget-filter-${item.name}-${index}`"
        >
          <div class="header" @click.prevent="handleOnClickHeader(item)">
            <div class="widget-title"> {{ item.title }} </div>
            <div class="state">
              <template v-if="!isFilled(item)">
                <div class="clear-state">
                  <FIcon
                    name="arrow-down"
                    :class="{ 'arrow-up': !isClosed(item), 'arrow-down': isClosed(item) }"
                    size="1rem"
                  />
                </div>
              </template>
              <template v-if="isFilled(item)">
                <div class="widget-clear">
                  {{ $t("advance_filter_clear") }}
                </div>
              </template>
            </div>
          </div>
          <slot :name="`advanced-column-${item.name}`" v-bind="item">
            <component
              v-if="isExpanded(item) || isFilled(item)"
              :column="item"
              :widget="item.getSelectedSuffix()"
              :operands="item.operands"
              :is="item.componentType"
            />
          </slot>
        </div>
      </div>

      <div class="drawer-footer">
        <Button
          outlined
          :disabled="!isChanged"
          class="header-btn clear-btn"
          :title="$t('clear_filter')"
          @click="handleOnClearAll"
        ></Button>
        <Button class="header-btn primary apply-btn" :title="$t('apply_filter')" @click="handleOnSaveForm"></Button>
      </div>
    </div>

    <v-overlay :absolute="true" :value="isLoading">
      <v-progress-circular :size="50" color="white" indeterminate />
    </v-overlay>
  </div>
</template>

<script>
import { $eventHub } from "@/components";
import * as yup from "yup";
import { mapActions, mapGetters } from "vuex";
import { EventConstraints } from "@/enums/events.enum";
import Drivers from "@/modules/settings/drivers";
import { Helper } from "@/services/helper";
import SingleTextWidget from "./widgets/single-text";
import RangeTextWidget from "./widgets/range-text";
import MultipleDropDownWidget from "./widgets/drop-down-multiple";
import { WIDGETS_TYPE, WIDGETS_STATE } from "@/enums/filter.enum";

export default {
  name: "FilterForm",

  components: { SingleTextWidget, RangeTextWidget, MultipleDropDownWidget },

  data() {
    return {
      pageTitle: this.$t("filter"),
      formSchema: null,
      isLoading: false,
      isChanged: false,
      isClearable: false,
    };
  },

  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    isFormEdit: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
  },

  mounted() {
    $eventHub.$on(EventConstraints.CLOSE_DRIVER_EDIT, this.closeDrawer);
  },

  destroyed() {
    $eventHub.$off(EventConstraints.CLOSE_DRIVER_EDIT);
  },

  watch: {
    columns: {
      immediate: true,
      deep: true,
      handler(columns) {
        if (columns.find((e) => e.value?.length > 0)) {
          this.isChanged = true;
        } else {
          this.isChanged = false;
        }
      },
    },
  },

  methods: {
    handleOnClickHeader(item) {
      if (this.isExpanded(item) || this.isFilled(item)) {
        return this.handleOnClear(item);
      }

      this.handleOnExpand(item);
    },

    /**
     * Handle toggle drawer and check if user made change
     * @return {void}
     */
    handleToggleDrawer() {
      $eventHub.$emit(EventConstraints.TOGGLE_APP_DRAWER);
    },

    /***
     * Handle on save the data
     * @return void
     */
    async handleOnSaveForm() {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.columns.forEach((column) => column.buildSchema());
      $eventHub.$emit(EventConstraints.UPDATE_QUERY_PARAMETERS);
      this.closeDrawer();
    },

    /**
     * will close the drawer and turn off the loading .2 seconds later to prevent the user from sending another request mean while
     * @return {void}
     */
    closeDrawer() {
      this.columns.forEach((column) => {
        if (!column.value || column.value?.length === 0) column.closWidget();
      });
      this.handleToggleDrawer();
      this.$nextTick(() => {
        setTimeout(() => {
          this.isLoading = false;
        }, 200);
      });
    },

    /**
     * change the state to Filled
     */
    handleOnExpand(widget) {
      widget.widgetState = WIDGETS_STATE.EXPANDED;
    },

    /**
     * change the state to closed , and clears value
     */
    handleOnClear(widget) {
      widget.value = "";
      widget.widgetState = WIDGETS_STATE.CLOSED;
      if (widget.type === WIDGETS_TYPE.SPECIAL) {
        $eventHub.$emit(EventConstraints.CLEAR_SPECIAL_COLUMN);
      }
    },

    handleOnClearAll() {
      this.columns.forEach((column) => this.handleOnClear(column));
    },

    /**
     * check if the widget is expanded
     */
    isExpanded(widget) {
      return widget.widgetState === WIDGETS_STATE.EXPANDED;
    },

    /**
     * check if widget is filled
     * @param widget
     * @returns {string}
     */
    isFilled(widget) {
      return widget.widgetState === WIDGETS_STATE.FILLED;
    },

    /**
     *   check if widget is close
     * @param widget
     * @returns {string}
     */
    isClosed(widget) {
      return widget.widgetState === WIDGETS_STATE.CLOSED;
    },
  },
};
</script>
<style lang="scss" src="./styles.scss" scoped />
